import { CreditCard, LaptopMinimal, Nfc } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import {
  type CardProgramme,
  getCardProgramme,
  selectSelectedPaymentMethod,
  useAvailablePaymentMethods,
} from "@/features/payment-methods";
import { useAppSelector } from "@/store";

import {
  OnboardingComponent,
  type OnboardingDataItem,
} from "../components/onboarding-component";
import { useCardClientName } from "../hooks/use-card-client-name";

export const CardOnboardingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);
  const clientName = useCardClientName();
  const { availablePaymentMethods, isLoadingAvailablePaymentMethods } =
    useAvailablePaymentMethods();

  // The first page introduces the programme the chosen card belongs to. A
  // selection that no longer matches an offered method reads as a Consumer
  // Card, which is what this page showed before the other programmes existed.
  const selectedMethod = availablePaymentMethods.find(
    (method) => method.method_provider_name === selectedPaymentMethod
  );
  const programme: CardProgramme =
    (selectedMethod && getCardProgramme(selectedMethod.payment_type)) ??
    "consumer";

  const introPageByProgramme: Record<CardProgramme, OnboardingDataItem> = {
    consumer: {
      title: t("cardOnboarding_page1_title"),
      description: [
        t("cardOnboarding_page1_description", { client: clientName }),
      ],
      imageSrc: "/card/visa-card.svg",
    },
    stipend: {
      title: t("cardOnboarding_page1_title"),
      description: [
        t("cardOnboarding_page1_stipend_description_1", { client: clientName }),
        t("cardOnboarding_page1_stipend_description_2"),
      ],
      imageSrc: "/card/visa-card.svg",
    },
    spend: {
      title: t("cardOnboarding_page1_spend_title", { client: clientName }),
      description: [
        t("cardOnboarding_page1_spend_description_1", { client: clientName }),
        t("cardOnboarding_page1_spend_description_2"),
      ],
      imageSrc: "/card/visa-card.svg",
    },
  };

  const data: OnboardingDataItem[] = [
    introPageByProgramme[programme],
    {
      title: t("cardOnboarding_page2_title", { client: clientName }),
      features: [
        {
          icon: <LaptopMinimal />,
          description: t("cardOnboarding_page2_shop_online"),
        },
        {
          icon: <Nfc />,
          description: t("cardOnboarding_page2_contactless"),
        },
        {
          icon: <CreditCard />,
          description: t("cardOnboarding_page2_chip_and_pin"),
          note: t("cardOnboarding_page2_physical_only"),
        },
        // PASS-577: Disable move money feature temporarily
        /* {
          icon: <Landmark />,
          description: t("cardOnboarding_page2_move_money"),
        }, */
      ],
      imageSrc: "/card/phone-shop.svg",
    },
    {
      title: t("cardOnboarding_page3_title"),
      description: [
        t("cardOnboarding_page3_description", { client: clientName }),
      ],
      features: [
        {
          description: t("cardOnboarding_page3_note_1"),
        },
        {
          description: t("cardOnboarding_page3_note_2"),
        },
        {
          description: t("cardOnboarding_page3_note_3"),
        },
      ],
      footer: t("cardOnboarding_page3_footer"),
      imageSrc: "/card/mobile-chart.svg",
    },
  ];

  const onFinish = () => {
    navigate(`/payment-methods/${selectedPaymentMethod}/create`);
  };

  // Rendering the wizard before the methods arrive would flash the Consumer
  // Card intro before switching to the chosen programme's copy.
  if (isLoadingAvailablePaymentMethods) {
    return (
      <div
        className="flex flex-col gap-y-4 p-4"
        role="status"
        aria-label={t("loading_text")}
      >
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    );
  }

  return (
    <OnboardingComponent
      data={data}
      finishLabel={t("vcc_createMyCard")}
      onFinish={onFinish}
    />
  );
};
